import React, { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import styles from './SimpleAccess.module.css';

// =============================================================================
// Simple access: every module as one row with View / Create / Edit / Delete,
// plus "Options" with that module's own switches and limits.
// It writes the same policy the server uses ({ module: { actions, constraints } }).
// Any blueprint action not listed below shows under "Other" in Options, so nothing is lost.
// =============================================================================

const COLS = [
    { id: 'view', label: 'View' },
    { id: 'create', label: 'Create' },
    { id: 'edit', label: 'Edit' },
    { id: 'delete', label: 'Delete' },
];

// action -> column, and the module options (grouped)
const MAP = {
    dashboard: {
        view: ['view'],
        options: [{ group: 'Cards on the dashboard', keys: ['view_financial_metrics', 'view_staff_metrics', 'view_recent_clients', 'view_live_board', 'view_appointment_metrics', 'send_whatsapp'] }],
    },
    billing: {
        view: ['view', 'view_history', 'view_bill'],
        create: ['create', 'complete_bill', 'edit_draft', 'cancel_draft'],
        edit: ['edit'],
        delete: ['cancel_invoice'],
        options: [
            { group: 'While billing', keys: ['change_bill_date', 'apply_discount', 'override_price', 'split_payment', 'credit_sale', 'collect_due', 'use_wallet', 'refund_payment'] },
            { group: 'Share with client', keys: ['send_whatsapp', 'send_whatsapp_api', 'send_sms', 'send_email'] },
            { group: 'Download & print', keys: ['download_bill', 'print_bill', 'export_history'] },
        ],
    },
    appointments: {
        view: ['view', 'view_booking_history'],
        create: ['create', 'checkout_booking'],
        edit: ['edit', 'reschedule', 'change_status'],
        delete: ['cancel_booking', 'delete'],
        options: [{ group: 'Appointments', keys: ['view_all_staff', 'send_whatsapp', 'send_staff_schedule'] }],
    },
    customers: {
        view: ['view', 'view_history'],
        create: ['create'],
        edit: ['edit'],
        delete: ['delete'],
        options: [{ group: 'Clients', keys: ['view_phone', 'view_spend', 'send_whatsapp', 'manage_wallet', 'merge_profiles'] }],
    },
    memberships: {
        view: ['view', 'view_discount', 'view_prepaid', 'view_passbook'],
        create: ['create_discount', 'create_prepaid'],
        edit: ['edit_discount', 'edit_prepaid'],
        delete: ['deactivate_discount', 'deactivate_prepaid'],
        options: [{ group: 'Selling', keys: ['sell_membership', 'renew', 'allot_cards', 'adjust_balance'] }],
    },
    packages: { view: ['view'], create: ['create'], edit: ['edit'], delete: ['deactivate'], options: [{ group: 'Selling', keys: ['sell_package'] }] },
    wallet: { view: ['view'], options: [{ group: 'Store wallet', keys: ['recharge'] }] },
    feedback: { view: ['view'], edit: ['reply'], delete: ['delete'] },
    pricelist: {
        view: ['view'],
        create: ['create', 'manage_categories', 'manage_addons'],
        edit: ['edit'],
        delete: ['delete'],
        options: [{ group: 'Price list', keys: ['edit_price', 'manage_taxes', 'import', 'export'] }],
    },
    inventory: {
        view: ['view', 'view_movements'],
        create: ['create'],
        edit: ['edit', 'bulk_update', 'adjust_stock'],
        delete: ['delete'],
        options: [{ group: 'Stock', keys: ['view_cost_price', 'view_stock_value', 'view_analytics', 'manage_suppliers'] }],
    },
    staff: {
        view: ['view', 'view_performance'],
        create: ['create'],
        edit: ['edit', 'edit_rules', 'edit_incentive', 'manage_roster'],
        delete: ['delete', 'reactivate'],
        options: [{ group: 'Salary', keys: ['view_pay', 'edit_pay'] }],
    },
    attendance: {
        view: ['view', 'view_all'],
        create: ['punch_self'],
        edit: ['edit_entries', 'mark_leave'],
        options: [{ group: 'Attendance', keys: ['punch_others', 'approve_overtime', 'view_salary_preview'] }],
    },
    payroll: {
        view: ['view', 'view_incentives'],
        create: ['create'],
        edit: ['edit', 'reopen'],
        options: [{ group: 'Payout', keys: ['process_payout', 'mark_paid', 'print_payslip', 'export', 'view_sensitive_pay'] }],
    },
    users: {
        view: ['view'],
        create: ['create'],
        edit: ['edit', 'reset_passwords', 'assign_branches'],
        delete: ['delete'],
        options: [{ group: 'Access', keys: ['manage_permissions', 'change_role'] }],
    },
    reports: {
        view: ['view'],
        options: [
            { group: 'Reports', keys: ['view_sales_analytics', 'view_collection_report', 'view_billing_report', 'view_gst_report', 'view_outstanding_analytics', 'view_service_analytics', 'view_client_analytics', 'view_membership_analytics', 'view_staff_analytics', 'view_staff_payout_report', 'view_expense_report', 'view_inventory_analytics', 'view_inventory_report', 'view_eod_report'] },
            { group: 'Download', keys: ['export_reports'] },
        ],
    },
    expense: { view: ['view'], create: ['create'], edit: ['edit'], delete: ['delete'], options: [{ group: 'Expenses', keys: ['approve', 'export'] }] },
    marketing: {
        view: ['view', 'view_audience', 'view_campaign_analytics'],
        create: ['create_campaign', 'manage_templates'],
        edit: ['edit_settings'],
        options: [{ group: 'Campaigns', keys: ['send_campaign', 'manage_global'] }],
    },
    integrations: {
        view: ['view'],
        options: [
            { group: 'WhatsApp', keys: ['view_whatsapp', 'edit_whatsapp'] },
            { group: 'SMS', keys: ['view_sms', 'edit_sms'] },
            { group: 'Email', keys: ['view_email', 'edit_email'] },
        ],
    },
    settings: {
        view: ['view', 'view_history'],
        edit: ['edit'],
        options: [{ group: 'Sections that can be saved', keys: ['edit_profile', 'edit_tax', 'edit_invoicing', 'edit_billing', 'edit_accounts', 'edit_appointments', 'edit_staff_policy', 'edit_communications', 'edit_security', 'manage_payment_modes', 'manage_global'] }],
    },
};

const RISK = { high: 'High risk', medium: 'Medium' };

export default function SimpleAccessEditor({ blueprint = [], categories = [], value = {}, onChange, limit = null, ceiling = null, readOnly = false, mode = 'USER' }) {
    const modules = useMemo(() => blueprint.filter((m) => !m.hqOnly), [blueprint]);
    const [open, setOpen] = useState({});

    const groups = useMemo(() => {
        const cats = categories.length ? categories : [{ id: 'all', name: 'Modules' }];
        return cats.map((c) => ({ ...c, mods: modules.filter((m) => (categories.length ? m.category === c.id : true)) })).filter((c) => c.mods.length);
    }, [categories, modules]);

    // column / options of a module, with any unmapped action added to "Other"
    const shapeOf = useMemo(() => {
        const out = {};
        modules.forEach((m) => {
            const map = MAP[m.id] || { view: ['view'] };
            const cols = {};
            COLS.forEach(({ id }) => { cols[id] = (map[id] || []).filter((k) => m.actions[k]); });
            const options = (map.options || []).map((g) => ({ ...g, keys: g.keys.filter((k) => m.actions[k]) })).filter((g) => g.keys.length);
            const used = new Set([...Object.values(cols).flat(), ...options.flatMap((g) => g.keys)]);
            const other = Object.keys(m.actions).filter((k) => !used.has(k));
            if (other.length) options.push({ group: 'Other', keys: other });
            out[m.id] = { cols, options };
        });
        return out;
    }, [modules]);

    const allowed = (mod, key) => !limit || limit?.[mod]?.actions?.[key] === true;
    const blockReason = (mod, key) => {
        if (ceiling && ceiling?.[mod]?.actions?.[key] === false) return 'Blocked by HQ';
        if (limit && limit?.[mod]?.actions?.[key] !== true) return mode === 'CEILING' ? 'Not allowed' : 'Above your access';
        return '';
    };
    const on = (mod, key) => value?.[mod]?.actions?.[key] === true;

    const write = (mod, actions, constraints) => {
        if (readOnly) return;
        const cur = value?.[mod] || { actions: {}, constraints: {} };
        onChange({ ...value, [mod]: { actions: { ...cur.actions, ...(actions || {}) }, constraints: { ...cur.constraints, ...(constraints || {}) } } });
    };

    /* ---------- columns ---------- */
    const colState = (m, col) => {
        const keys = shapeOf[m.id].cols[col].filter((k) => allowed(m.id, k) || on(m.id, k));
        if (!shapeOf[m.id].cols[col].length) return 'NA';
        if (!keys.length) return 'LOCKED';
        const n = keys.filter((k) => on(m.id, k)).length;
        return n === 0 ? 'OFF' : n === keys.length ? 'ON' : 'SOME';
    };

    const setCol = (m, col, turnOn) => {
        const actions = {};
        if (col === 'view' && !turnOn) {
            Object.keys(m.actions).forEach((k) => { actions[k] = false; });     // view off = whole module off
        } else {
            shapeOf[m.id].cols[col].forEach((k) => { actions[k] = turnOn && allowed(m.id, k); });
            if (turnOn && m.actions.view && allowed(m.id, 'view')) actions.view = true;  // anything on needs the module open
        }
        write(m.id, actions);
    };

    const setOne = (m, key, turnOn) => {
        if (turnOn && !allowed(m.id, key)) return;
        const actions = { [key]: turnOn };
        if (turnOn && key !== 'view' && m.actions.view && allowed(m.id, 'view')) actions.view = true;
        write(m.id, actions);
    };

    /* ---------- limits ---------- */
    const constraintLimit = (mod, key) => (limit ? limit?.[mod]?.constraints?.[key] : undefined);
    const setRule = (m, key, def, raw) => {
        const lim = constraintLimit(m.id, key);
        let v = raw;
        if (def.type === 'toggle') { if (raw && lim === false) return; v = Boolean(raw); }
        if (def.type === 'number') {
            v = Math.max(0, Number(raw) || 0);
            if (lim !== undefined && Number(lim) > 0 && (v === 0 || v > Number(lim))) v = Number(lim);
        }
        if (def.type === 'select' && lim !== undefined && Number(v) > Number(lim)) v = String(lim);
        write(m.id, null, { [key]: v });
    };

    const optionCount = (m) => {
        const keys = shapeOf[m.id].options.flatMap((g) => g.keys);
        return { on: keys.filter((k) => on(m.id, k)).length, total: keys.length, rules: Object.keys(m.constraints || {}).length };
    };

    return (
        <div className={styles.wrap}>
            <div className={styles.legend}>
                <span><b>View</b> open and see</span>
                <span><b>Create</b> add new</span>
                <span><b>Edit</b> change</span>
                <span><b>Delete</b> remove / cancel</span>
                <span><b>Options</b> special switches and limits</span>
            </div>

            <div className={styles.table} role="table" aria-label="Access by module">
                <div className={`${styles.tr} ${styles.thead}`} role="row">
                    <span role="columnheader">Module</span>
                    {COLS.map((c) => <span key={c.id} role="columnheader" className={styles.center}>{c.label}</span>)}
                    <span role="columnheader" className={styles.center}>Options</span>
                </div>

                {groups.map((g) => (
                    <Fragment key={g.id}>
                        <div className={styles.groupRow} role="row"><span>{g.name}</span></div>
                        {g.mods.map((m) => {
                            const isOpen = Boolean(open[m.id]);
                            const moduleOn = on(m.id, 'view');
                            const blocked = !allowed(m.id, 'view') ? blockReason(m.id, 'view') : '';
                            const count = optionCount(m);
                            const hasOptions = count.total > 0 || count.rules > 0;
                            return (
                                <Fragment key={m.id}>
                                    <div className={`${styles.tr} ${moduleOn ? '' : styles.trOff} ${isOpen ? styles.trOpen : ''}`} role="row">
                                        <span className={styles.modName}>
                                            <strong>{m.name}</strong>
                                            {blocked && <em className={styles.tagLock}><Lock size={9} /> {blocked}</em>}
                                        </span>
                                        {COLS.map((c) => {
                                            const st = colState(m, c.id);
                                            if (st === 'NA') return <span key={c.id} className={`${styles.center} ${styles.na}`} aria-label="Not used">—</span>;
                                            if (st === 'LOCKED') return <span key={c.id} className={styles.center} title={blockReason(m.id, shapeOf[m.id].cols[c.id][0])}><Lock size={12} className={styles.lockIcon} /></span>;
                                            return (
                                                <span key={c.id} className={styles.center}>
                                                    <input
                                                        type="checkbox"
                                                        className={styles.box}
                                                        checked={st === 'ON'}
                                                        ref={(el) => { if (el) el.indeterminate = st === 'SOME'; }}
                                                        disabled={readOnly}
                                                        onChange={() => setCol(m, c.id, st !== 'ON')}
                                                        aria-label={`${m.name}: ${c.label}`}
                                                        title={st === 'SOME' ? 'Partly on: open Options or Advanced to see which' : undefined}
                                                    />
                                                </span>
                                            );
                                        })}
                                        <span className={styles.center}>
                                            {hasOptions ? (
                                                <button type="button" className={`${styles.optBtn} ${isOpen ? styles.optBtnOn : ''}`} onClick={() => setOpen((o) => ({ ...o, [m.id]: !o[m.id] }))} aria-expanded={isOpen}>
                                                    {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                    {count.total ? `${count.on}/${count.total}` : 'Limits'}
                                                </button>
                                            ) : <span className={styles.na}>—</span>}
                                        </span>
                                    </div>

                                    {isOpen && hasOptions && (
                                        <div className={styles.panel} role="row">
                                            {shapeOf[m.id].options.map((grp) => (
                                                <div key={grp.group} className={styles.optGroup}>
                                                    <span className={styles.optGroupName}>{grp.group}</span>
                                                    <div className={styles.optGrid}>
                                                        {grp.keys.map((k) => {
                                                            const def = m.actions[k];
                                                            const isOnNow = on(m.id, k);
                                                            const reason = !allowed(m.id, k) ? blockReason(m.id, k) : '';
                                                            return (
                                                                <label key={k} className={`${styles.opt} ${isOnNow ? styles.optOn : ''} ${def.risk === 'high' && isOnNow ? styles.optRisk : ''}`} title={reason || def.desc}>
                                                                    <input type="checkbox" className={styles.box} checked={isOnNow} disabled={readOnly || (!isOnNow && !allowed(m.id, k))} onChange={(e) => setOne(m, k, e.target.checked)} />
                                                                    <span className={styles.optText}>
                                                                        <strong>{def.label}{RISK[def.risk] && <em className={def.risk === 'high' ? styles.riskHigh : styles.riskMed}>{RISK[def.risk]}</em>}</strong>
                                                                        <small>{reason || def.desc}</small>
                                                                    </span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}

                                            {count.rules > 0 && (
                                                <div className={styles.optGroup}>
                                                    <span className={styles.optGroupName}>Limits</span>
                                                    <div className={styles.optGrid}>
                                                        {Object.entries(m.constraints).map(([key, def]) => {
                                                            const v = value?.[m.id]?.constraints?.[key] ?? def.value;
                                                            return (
                                                                <label key={key} className={styles.limit} title={def.desc}>
                                                                    <span className={styles.optText}>
                                                                        <strong>{def.label}</strong>
                                                                        <small>{def.desc}</small>
                                                                    </span>
                                                                    {def.type === 'toggle' && (
                                                                        <input type="checkbox" className={styles.box} checked={Boolean(v)} disabled={readOnly || (!v && constraintLimit(m.id, key) === false)} onChange={(e) => setRule(m, key, def, e.target.checked)} />
                                                                    )}
                                                                    {def.type === 'select' && (
                                                                        <select className={styles.input} value={String(v)} disabled={readOnly} onChange={(e) => setRule(m, key, def, e.target.value)}>
                                                                            {def.options.map((o) => <option key={o.value} value={o.value} disabled={constraintLimit(m.id, key) !== undefined && Number(o.value) > Number(constraintLimit(m.id, key))}>{o.label}</option>)}
                                                                        </select>
                                                                    )}
                                                                    {def.type === 'number' && (
                                                                        <span className={styles.numWrap}>
                                                                            <input className={styles.input} type="number" min="0" value={v} disabled={readOnly} onChange={(e) => setRule(m, key, def, e.target.value)} />
                                                                            <small>{def.unit}{Number(v) === 0 ? ' (no limit)' : ''}</small>
                                                                        </span>
                                                                    )}
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Fragment>
                            );
                        })}
                    </Fragment>
                ))}
            </div>
        </div>
    );
}